import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Banner, Checkbox, Tab, Typography } from '@scale/scaleui';
import { ChatStoreAwareConfigurationPresets } from '../../ConfigurationPresets/ChatStoreAwareConfigurationPresets';
import ChatTaskFeatureConfig from 'react-scale/corp/views/CorpProjectGroupManager/views/Linter/GenericLinterView/ChatTaskFeatureConfig';
import ChatConfigurationEditor from '../../ChatConfigurationEditor';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';

import { chatParamsSchema, formatChatJoiValidateError } from '@scale/shared/chat/validation';
import { useChatStoreExtract } from '../../../Chat/hooks/ChatStore.hooks';
import { QueryClientProvider } from '@tanstack/react-query-v4';
import { queryClient } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';

type ConfigPageProps = {
  restrictRollbacks: boolean;
  chatTaskFeatureConfig: IChatTaskFeatureConfig;
  setRestrictRollbacks: Dispatch<SetStateAction<boolean>>;
  setChatTaskFeatureConfig: Dispatch<SetStateAction<IChatTaskFeatureConfig>>;
};

export const ConfigPage: FC<ConfigPageProps> = ({
  restrictRollbacks,
  chatTaskFeatureConfig,
  setRestrictRollbacks,
  setChatTaskFeatureConfig,
}) => {
  const [schemaValidationErrors, setSchemaValidationErrors] = useState<string[]>([]);
  const config = useChatStoreExtract(state => state.config);
  const setConfig = useChatStoreExtract(state => state.setConfig);
  const setResponses = useChatStoreExtract(state => state.setResponses);

  return (
    <Tab.Panel>
      <div className="flex gap-12 px-12">
        <div>
          <div className="flex flex-col gap-2 items-start py-4">
            <Typography.Subtitle1>Interface</Typography.Subtitle1>
            <Checkbox
              id="restrict-rollbacks"
              label="Restrict rollbacks"
              checked={restrictRollbacks}
              onChange={() => setRestrictRollbacks(prev => !prev)}
            />

            <div className="w-80">
              <ChatTaskFeatureConfig
                value={chatTaskFeatureConfig}
                setValue={setChatTaskFeatureConfig}
                omitCopyAndPasteConfigs={false}
              />
            </div>
            <ChatStoreAwareConfigurationPresets title="Saved" />
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 py-4">
          {schemaValidationErrors.length > 0 && (
            <div className="flex flex-col gap-2 w-full">
              {schemaValidationErrors.map((e, i) => (
                <Banner
                  key={i}
                  title="Validation Error"
                  subtitle={e}
                  variant="warning"
                  background="neutral"
                  className="my-2"
                />
              ))}
            </div>
          )}
          <QueryClientProvider client={queryClient}>
            <ChatConfigurationEditor
              value={config}
              onSubmit={config => {
                setResponses([]);
                const { error } = chatParamsSchema.validate(config);
                if (error) {
                  setSchemaValidationErrors(formatChatJoiValidateError(error, config));
                } else {
                  setSchemaValidationErrors([]);
                }
                setConfig(config);
              }}
            />
          </QueryClientProvider>
        </div>
      </div>
    </Tab.Panel>
  );
};
